import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import List from '@mui/material/List';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { P, match } from 'ts-pattern';

import { FilterCheckbox } from '#/src/components/common/Filter';
import {
  SuodatinAccordion,
  SuodatinAccordionDetails,
  SuodatinAccordionSummary,
} from '#/src/components/common/Filter/CustomizedMuiComponents';
import { NumberRangeSlider } from '#/src/components/common/Filter/NumberRangeSlider';
import { SummaryContent } from '#/src/components/common/Filter/SummaryContent';
import { MaterialIcon } from '#/src/components/common/MaterialIcon';
import { useConfig } from '#/src/config';
import { RAJAIN_TYPES } from '#/src/constants';
import {
  getStateChangesForCheckboxRajaimet,
  isChecked,
  useRajainItems,
} from '#/src/tools/filters';
import {
  RajainComponentProps,
  RajainItem,
  NumberRangeRajainId,
} from '#/src/types/SuodatinTypes';

import { marks } from './maksullisuusRajainUtils';

type MaksunMaaraRajainName =
  | 'maksunmaara'
  | 'lukuvuosimaksunmaara_kk'
  | 'lukuvuosimaksunmaara_amm_lk';

const getCheckboxRajain = (rajainItems: Array<RajainItem>, id: string): RajainItem =>
  rajainItems.find((r) => r.id === id) ?? {
    rajainId: 'maksullisuustyyppi',
    id,
    count: 0,
    checked: false,
  };

const getNumberRangeRajain = (
  rajainItems: Array<RajainItem>,
  rajainId: NumberRangeRajainId
) =>
  rajainItems?.find((r) => r.rajainId === rajainId) ?? {
    rajainId,
    id: rajainId,
    count: 0,
  };

type NumberRangeValues = {
  undefinedValue: Array<number>;
  numberRangeValues: Array<number>;
};

const getNumberRangeValues = (
  numberRangeRajain: RajainItem,
  defaultUpperLimit: number = 0
): NumberRangeValues => {
  const { upperLimit, min, max } = match(numberRangeRajain)
    .with(
      {
        upperLimit: P.optional(P.number),
        min: P.optional(P.number),
        max: P.optional(P.number),
      },
      (v) => ({
        upperLimit: v.upperLimit,
        min: v.min,
        max: v.max,
      })
    )
    .run();
  const undefinedValue = [0, upperLimit ?? defaultUpperLimit];
  return {
    undefinedValue,
    numberRangeValues: [min || undefinedValue[0], max || undefinedValue[1]],
  };
};

const rangeRajainObject = (rajainName: string, rangeValues: Array<number>) => ({
  [rajainName]: {
    [`${rajainName}_min`]: rangeValues[0],
    [`${rajainName}_max`]: rangeValues[1],
  },
});

const maksullisuustyyppiSetDisabled = (
  rajainItems: Array<RajainItem>,
  newRajainValues: Array<string>,
  tyyppi: string
) =>
  isChecked(getCheckboxRajain(rajainItems, tyyppi)) && !newRajainValues.includes(tyyppi);

const maksullisuustyyppiRajainItems = (rajainItems: Array<RajainItem>) =>
  ['maksuton', 'maksullinen', 'lukuvuosimaksu_kk', 'lukuvuosimaksu_amm_lk'].map(
    (tyyppi) => getCheckboxRajain(rajainItems, tyyppi)
  );

const labelText = (val: number) => (val > 0 ? `${val}€` : '0');
const rangeText = (v: NumberRangeValues): string =>
  match(v)
    .when(
      () => isEqual(v.numberRangeValues, v.undefinedValue),
      () => ''
    )
    .with(
      { numberRangeValues: P.select(P.array(P.number)) },
      (values) =>
        ` ${labelText(values[0])}` +
        (values[0] === values[1] ? '' : ` - ${labelText(values[1])}`)
    )
    .otherwise(() => '');

const maksullisuustyyppiChanges = (
  rajainItems: Array<RajainItem>,
  rajainItem: RajainItem
) => {
  let changes = getStateChangesForCheckboxRajaimet(
    maksullisuustyyppiRajainItems(rajainItems)
  )(rajainItem);

  if (
    maksullisuustyyppiSetDisabled(rajainItems, changes.maksullisuustyyppi, 'maksullinen')
  ) {
    changes = Object.assign(changes, rangeRajainObject('maksunmaara', [0, 0]));
  }
  if (
    maksullisuustyyppiSetDisabled(
      rajainItems,
      changes.maksullisuustyyppi,
      'lukuvuosimaksu_kk'
    )
  ) {
    changes = Object.assign(
      changes,
      rangeRajainObject('lukuvuosimaksunmaara_kk', [0, 0]),
      {
        apuraha: false,
      }
    );
  }

  if (
    maksullisuustyyppiSetDisabled(
      rajainItems,
      changes.maksullisuustyyppi,
      'lukuvuosimaksu_amm_lk'
    )
  ) {
    changes = Object.assign(
      changes,
      rangeRajainObject('lukuvuosimaksunmaara_amm_lk', [0, 0])
    );
  }
  return changes;
};

const useNumberRangeValues = (
  rajainName: MaksunMaaraRajainName,
  rajainItems: Array<RajainItem>
) =>
  useMemo(
    () => getNumberRangeValues(getNumberRangeRajain(rajainItems, rajainName)),
    [rajainItems, rajainName]
  );

const MaksullisuusRangeSlider = ({
  rajainName,
  rajainItems = [],
  setRajainValues,
}: Pick<RajainComponentProps, 'setRajainValues'> & {
  rajainItems?: Array<RajainItem>;
  rajainName: MaksunMaaraRajainName;
}) => {
  const { undefinedValue, numberRangeValues } = useNumberRangeValues(
    rajainName,
    rajainItems
  );
  const setMaksuRange = (maksuRange: Array<number>) =>
    setRajainValues(
      rangeRajainObject(
        rajainName,
        isEqual(maksuRange, undefinedValue) ? undefinedValue : maksuRange
      )
    );

  // Näytetään slider vain, jos sille on tullut konfo-backendiltä jokin järkevä yläraja (oletus nolla)
  // Nolla tarkoittaa ettei dataa ei ole saatu ladattua, tai rajaimen millekään arvolle ei ole olemassa yhtään tulosta.
  return undefinedValue?.[1] === 0 ? null : (
    <Box mr={2} ml={1}>
      <NumberRangeSlider
        values={numberRangeValues}
        min={undefinedValue[0]}
        max={undefinedValue[1]}
        marks={marks(undefinedValue[1])}
        labelFormatter={labelText}
        onRangeCommit={setMaksuRange}
      />
    </Box>
  );
};

const MaksullisuusSummary = ({
  rajainItems = [],
  displaySelected,
}: Pick<RajainComponentProps, 'displaySelected'> & {
  rajainItems?: Array<RajainItem>;
}) => {
  const { t } = useTranslation();
  const maksunmaara = useNumberRangeValues(RAJAIN_TYPES.MAKSUNMAARA, rajainItems);
  const lukuvuosimaksuKkMaksunMaara = useNumberRangeValues(
    RAJAIN_TYPES.LUKUVUOSIMAKSUNMAARA_KK,
    rajainItems
  );
  const lukuvuosimaksuAmmLkMaksunMaara = useNumberRangeValues(
    RAJAIN_TYPES.LUKUVUOSIMAKSUNMAARA_AMM_LK,
    rajainItems
  );
  const apuraha = getCheckboxRajain(rajainItems, 'apuraha');

  const selectedRajainItems =
    maksullisuustyyppiRajainItems(rajainItems).filter(isChecked);

  const contentString = useMemo(
    () =>
      selectedRajainItems
        .map(
          (item) =>
            t(`haku.${item.id}`) +
            match(item)
              .with({ id: 'maksullinen' }, () => rangeText(maksunmaara))
              .with(
                { id: 'lukuvuosimaksu_kk' },
                () =>
                  rangeText(lukuvuosimaksuKkMaksunMaara) +
                  (isChecked(apuraha) ? t('haku.apuraha') : '')
              )
              .with({ id: 'lukuvuosimaksu_amm_lk' }, () =>
                rangeText(lukuvuosimaksuAmmLkMaksunMaara)
              )
              .otherwise(() => '')
        )
        .join(','),
    [
      selectedRajainItems,
      t,
      maksunmaara,
      lukuvuosimaksuKkMaksunMaara,
      apuraha,
      lukuvuosimaksuAmmLkMaksunMaara,
    ]
  );

  return (
    <SuodatinAccordionSummary expandIcon={<MaterialIcon icon="expand_more" />}>
      <SummaryContent
        filterName={t('haku.maksullisuus')}
        contentString={contentString}
        numberOfItems={selectedRajainItems.length}
        displaySelected={displaySelected}
      />
    </SuodatinAccordionSummary>
  );
};

type InputsSectionProps = Pick<RajainComponentProps, 'setRajainValues'> & {
  rajainItems?: Array<RajainItem>;
  isCountVisible?: boolean;
};

const MaksullinenInputs = ({
  rajainItems = [],
  setRajainValues,
  isCountVisible,
}: InputsSectionProps) => (
  <>
    <FilterCheckbox
      value={getCheckboxRajain(rajainItems, 'maksullinen')}
      onChange={(item) => setRajainValues(maksullisuustyyppiChanges(rajainItems, item))}
      isCountVisible={isCountVisible}
    />
    {isChecked(getCheckboxRajain(rajainItems, 'maksullinen')) && (
      <MaksullisuusRangeSlider
        rajainName="maksunmaara"
        setRajainValues={setRajainValues}
        rajainItems={rajainItems}
      />
    )}
  </>
);

const LukuvuosimaksuInputs = ({
  rajainItems = [],
  setRajainValues,
  isCountVisible,
}: InputsSectionProps) => {
  return (
    <>
      <Box>
        <FilterCheckbox
          value={getCheckboxRajain(rajainItems, 'lukuvuosimaksu_kk')}
          onChange={(item) =>
            setRajainValues(maksullisuustyyppiChanges(rajainItems, item))
          }
          isCountVisible={isCountVisible}
        />
      </Box>
      {isChecked(getCheckboxRajain(rajainItems, 'lukuvuosimaksu_kk')) && (
        <MaksullisuusRangeSlider
          rajainName="lukuvuosimaksunmaara_kk"
          rajainItems={rajainItems}
          setRajainValues={setRajainValues}
        />
      )}
      <FilterCheckbox
        key={RAJAIN_TYPES.APURAHA}
        value={getCheckboxRajain(rajainItems, 'apuraha')}
        onChange={(item: RajainItem) =>
          setRajainValues({
            apuraha: !isChecked(item),
          })
        }
        isCountVisible={false}
        disabled={!isChecked(getCheckboxRajain(rajainItems, 'lukuvuosimaksu_kk'))}
      />
    </>
  );
};

const LukuvuosimaksuAmmLk = ({
  rajainItems = [],
  setRajainValues,
  isCountVisible,
}: InputsSectionProps) => {
  return (
    <>
      <Box>
        <FilterCheckbox
          value={getCheckboxRajain(rajainItems, 'lukuvuosimaksu_amm_lk')}
          onChange={(item) =>
            setRajainValues(maksullisuustyyppiChanges(rajainItems, item))
          }
          isCountVisible={isCountVisible}
        />
      </Box>
      {isChecked(getCheckboxRajain(rajainItems, 'lukuvuosimaksu_amm_lk')) && (
        <MaksullisuusRangeSlider
          rajainName="lukuvuosimaksunmaara_amm_lk"
          rajainItems={rajainItems}
          setRajainValues={setRajainValues}
        />
      )}
    </>
  );
};

export const MaksullisuusSuodatin = ({
  summaryHidden,
  displaySelected = true,
  elevation = 0,
  expanded,
  rajainOptions,
  rajainValues,
  setRajainValues,
}: RajainComponentProps) => {
  const config = useConfig();
  const isCountVisible = config?.naytaFiltterienHakutulosLuvut;

  const rajainItems = useRajainItems(
    rajainOptions,
    rajainValues,
    RAJAIN_TYPES.MAKSULLISUUS
  );

  return (
    <SuodatinAccordion elevation={elevation} defaultExpanded={expanded} square>
      {!summaryHidden && (
        <MaksullisuusSummary
          rajainItems={rajainItems}
          displaySelected={displaySelected}
        />
      )}
      <SuodatinAccordionDetails {...(summaryHidden && { style: { padding: 0 } })}>
        <List sx={{ width: '100%' }}>
          <FilterCheckbox
            value={getCheckboxRajain(rajainItems, 'maksuton')}
            onChange={(item) =>
              setRajainValues(maksullisuustyyppiChanges(rajainItems, item))
            }
            isCountVisible={isCountVisible}
          />
          <MaksullinenInputs
            rajainItems={rajainItems}
            setRajainValues={setRajainValues}
            isCountVisible={isCountVisible}
          />
          <LukuvuosimaksuAmmLk
            rajainItems={rajainItems}
            setRajainValues={setRajainValues}
            isCountVisible={isCountVisible}
          />
          <LukuvuosimaksuInputs
            rajainItems={rajainItems}
            setRajainValues={setRajainValues}
            isCountVisible={isCountVisible}
          />
        </List>
      </SuodatinAccordionDetails>
    </SuodatinAccordion>
  );
};
